/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ComponentProps, forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/*
 * Variant → Figma token mapping (CRCB2B-626). Mirrors the SRAM Figma type system:
 * DISPLAY (lg/md), HEADING (xl→2xs mapped to h1→h6), and BODY (large/default/small).
 * Single family (Neue Haas Grotesk Display Pro); the variants below use font-normal
 * (400), font-book (450), and font-medium (500). Line-heights and letter-spacing
 * (tracking-sram = 0.03em, -tight = 0.02em) come from the Figma tokens.
 * Sizes: 9xl 96 · 8xl 80 · 7xl 64 · 6xl 56 · 4xl 40 · 3xl 32 · 2xl 24 · lg 18 ·
 * md 16 · sm 14.
 */
const typographyVariants = cva('', {
    variants: {
        variant: {
            // Display (above the heading scale)
            'display-lg': 'text-9xl font-normal leading-[1.1] tracking-sram', // display/lg 96
            'display-md': 'text-8xl font-normal leading-[1.1] tracking-sram', // display/md 80
            // Headings — Figma heading/xl → heading/2xs
            h1: 'text-7xl font-normal leading-[1.1] tracking-sram', // heading/xl 64
            h2: 'text-6xl font-book leading-tight tracking-sram', // heading/lg 56, lh 1.25
            h3: 'text-4xl font-book leading-[1.1] tracking-sram', // heading/md 40
            h4: 'text-3xl font-medium leading-[1.3] tracking-sram', // heading/sm 32
            h5: 'text-2xl font-medium leading-[1.3] tracking-sram', // heading/xs 24
            h6: 'text-lg font-medium leading-[1.3] tracking-sram-tight', // heading/2xs 18, ls 2px (absolute — see tailwind.css)
            // Body scale
            large: 'text-lg font-book leading-normal tracking-sram', // body/lg 18
            p: 'text-md font-book leading-normal tracking-sram [&:not(:first-child)]:mt-6', // body/default 16
            small: 'text-sm font-book leading-normal tracking-sram', // body/sm 14
            // Body color modifier (not a size step)
            muted: 'text-sm font-book leading-normal tracking-sram text-muted-foreground',
            blockquote: 'mt-6 border-l-2 pl-6 italic',
            'inline-code': 'relative rounded-ui bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-medium',
            // Product-specific variants (unchanged — not part of the Figma type scale)
            'product-title': 'text-sm font-medium text-foreground leading-tight',
            'product-price': 'text-md font-medium text-foreground',
            'product-description': 'text-sm text-muted-foreground leading-relaxed',
            'recommendation-title': 'self-stretch text-foreground text-3xl font-medium leading-none tracking-sram',
        },
        align: {
            left: 'text-left',
            center: 'text-center',
            right: 'text-right',
        },
    },
    defaultVariants: {
        variant: 'p',
        align: 'left',
    },
});

interface TypographyProps extends ComponentProps<'div'>, VariantProps<typeof typographyVariants> {
    asChild?: boolean;
    as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div' | 'blockquote' | 'code' | 'ul' | 'ol' | 'li';
}

const Typography = forwardRef<HTMLDivElement, TypographyProps>(
    ({ className, variant, align, asChild = false, as, children, ...props }, ref) => {
        const Comp = asChild ? Slot : as || getDefaultElement(variant);

        return (
            <Comp className={cn(typographyVariants({ variant, align }), className)} ref={ref} {...props}>
                {children}
            </Comp>
        );
    }
);

Typography.displayName = 'Typography';

// Helper function to get default element based on variant
function getDefaultElement(variant: TypographyProps['variant']): string {
    switch (variant) {
        // Display is a visual treatment, not a semantic level — default to the
        // top heading tags but authors can override via `as`.
        case 'display-lg':
            return 'h1';
        case 'display-md':
            return 'h2';
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
            return variant;
        case 'blockquote':
            return 'blockquote';
        case 'inline-code':
            return 'code';
        case 'product-title':
            return 'h3';
        case 'product-price':
        case 'product-description':
        case 'p':
        case 'large':
        case 'small':
        case 'muted':
        default:
            return 'p';
    }
}

// eslint-disable-next-line react-refresh/only-export-components
export { Typography, typographyVariants };
export type { TypographyProps };
